import { useState } from 'react';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/core/Accordion';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetFilterGroup } from '@/pages/dam/api';
import type { DamFilterValues } from '@/pages/dam/utils/damLibraryUrlState';

import { DamPanelOption } from '../DamPanelOption';
import { DamSidePanel } from '../DamSidePanel';

interface DamFilterPanelProps {
    /** Groups of the `asset_filter` layout, in layout order (D-022). */
    groups: AssetFilterGroup[];
    filters: DamFilterValues;
    /** Called with the complete next filter state; changes apply at once. */
    onChange: (filters: DamFilterValues) => void;
    onClose: () => void;
}

const groupTriggerClassName = 'tw-py-3 tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base';

const toggleValue = (filters: DamFilterValues, attributeName: string, value: string): DamFilterValues => {
    const current = filters[attributeName] ?? [];
    const nextValues = current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];
    const next = { ...filters };
    if (nextValues.length > 0) {
        next[attributeName] = nextValues;
    } else {
        delete next[attributeName];
    }
    return next;
};

/**
 * Filter panel of the library (wireframe `I6392:31622;6476:92549`): one collapsible
 * group per `asset_filter` attribute, all open initially. Values within a group are
 * OR-ed, groups are AND-ed (API behaviour).
 */
function DamFilterPanel({ groups, filters, onChange, onClose }: DamFilterPanelProps) {
    const { t } = useTranslation();
    const [closedGroups, setClosedGroups] = useState<string[]>([]);
    const groupNames = groups.map((group) => group.attributeName);
    const openGroups = groupNames.filter((name) => !closedGroups.includes(name));
    const handleOpenGroupsChange = (open: string[]) => setClosedGroups(groupNames.filter((name) => !open.includes(name)));

    return (
        <DamSidePanel title={t('DAM.LIBRARY.FILTER')} onClose={onClose}>
            {groups.length === 0 ? (
                <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.LIBRARY.NO_FILTER_GROUPS')}</p>
            ) : (
                <Accordion type="multiple" value={openGroups} onValueChange={handleOpenGroupsChange}>
                    {groups.map((group) => (
                        <AccordionItem
                            key={group.attributeName}
                            value={group.attributeName}
                            className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-divider"
                        >
                            <AccordionTrigger className={groupTriggerClassName}>
                                {group.label}
                            </AccordionTrigger>
                            <AccordionContent>
                                <ul className="tw-m-0 tw-list-none tw-p-0 tw-pb-3">
                                    {group.options.map((option) => (
                                        <li key={option.key}>
                                            <DamPanelOption
                                                label={option.label}
                                                isSelected={filters[group.attributeName]?.includes(option.key) ?? false}
                                                onToggle={() => onChange(toggleValue(filters, group.attributeName, option.key))}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            )}
        </DamSidePanel>
    );
}

export { DamFilterPanel, toggleValue };
export type { DamFilterPanelProps };
